import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Clock, CheckCircle, AlertCircle, TrendingUp,
  Calendar, ChevronRight, Play, Pause, Check, X, Eye
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_STATUS = {
  pending: { label: "Pendente", icon: Clock, color: "text-neutral-400", bg: "bg-neutral-100", border: "border-neutral-200" },
  in_progress: { label: "Em andamento", icon: TrendingUp, color: "text-amber-500", bg: "bg-amber-50", border: "border-amber-200" },
  completed: { label: "Concluído", icon: CheckCircle, color: "text-blue-500", bg: "bg-blue-50", border: "border-blue-200" },
  approved: { label: "Aprovado", icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  approved_with_reservations: { label: "Aprovado c/ Ressalvas", icon: AlertCircle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  rejected: { label: "Rejeitado", icon: X, color: "text-red-500", bg: "bg-red-50", border: "border-red-200" }
};

export default function ProjectTimelinePage() {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStage, setSelectedStage] = useState(null);
  const [showMeasurement, setShowMeasurement] = useState(false);

  useEffect(() => {
    fetchStages();
  }, [projectId, token]);

  const fetchStages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/stages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setStages(await res.json());
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Erro ao carregar cronograma");
    } finally {
      setLoading(false);
    }
  };

  const handleStartStage = async (stageId) => {
    try {
      const res = await fetch(`${API_URL}/api/stages/${stageId}/start`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      if (res.ok) {
        toast.success("Etapa iniciada!");
        fetchStages();
      }
    } catch (error) {
      toast.error("Erro ao iniciar etapa");
    }
  };

  const handleCompleteStage = async (stageId) => {
    try {
      const res = await fetch(`${API_URL}/api/stages/${stageId}/complete`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      if (res.ok) {
        toast.success("Etapa concluída! Aguardando medição.");
        fetchStages();
      }
    } catch (error) {
      toast.error("Erro ao concluir etapa");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen-mobile flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen-mobile bg-white pb-nav" data-testid="project-timeline-page">
      {/* Header */}
      <header className="sticky-header border-b border-neutral-100">
        <div className="px-5 h-14 flex items-center gap-3">
          <button 
            onClick={() => navigate(`/project/${projectId}`)}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold text-neutral-900">Cronograma da Obra</h1>
            <p className="text-xs text-neutral-500">{stages.length} etapas</p>
          </div>
        </div>
      </header>

      <main className="px-5 py-6">
        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-neutral-200" />

          <div className="space-y-6">
            {stages.map((stage, index) => {
              const status = STAGE_STATUS[stage.status] || STAGE_STATUS.pending;
              const StatusIcon = status.icon;
              const isActive = stage.status === "in_progress";
              const canStart = stage.status === "pending" && 
                (index === 0 || stages[index - 1]?.status === "approved");
              const canComplete = stage.status === "in_progress" && user?.role === "provider";
              const canMeasure = stage.status === "completed" && user?.role === "client";

              return (
                <div key={stage.stage_id} className="relative pl-12 animate-up" style={{ animationDelay: `${index * 50}ms` }}>
                  {/* Timeline Dot */}
                  <div className={`absolute left-2.5 w-5 h-5 rounded-full ${status.bg} border-2 ${status.border} flex items-center justify-center`}>
                    {isActive && <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />}
                  </div>

                  {/* Stage Card */}
                  <div className={`card-clean p-4 ${isActive ? 'border-amber-300' : ''}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-neutral-500">Etapa {stage.order}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                            {status.label}
                          </span>
                        </div>
                        <h3 className="font-semibold text-neutral-900">{stage.name}</h3>
                        {stage.description && (
                          <p className="text-sm text-neutral-500 mt-1">{stage.description}</p>
                        )}

                        {/* Dates */}
                        <div className="flex items-center gap-4 mt-3 text-xs text-neutral-500">
                          {stage.estimated_start && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span>Início: {new Date(stage.estimated_start).toLocaleDateString("pt-BR")}</span>
                            </div>
                          )}
                          {stage.estimated_end && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span>Fim: {new Date(stage.estimated_end).toLocaleDateString("pt-BR")}</span>
                            </div>
                          )}
                        </div>

                        {/* Cost */}
                        {stage.estimated_cost > 0 && (
                          <p className="text-sm text-neutral-700 mt-2">
                            Custo: <span className="font-medium">R$ {stage.estimated_cost.toLocaleString("pt-BR")}</span>
                          </p>
                        )}
                      </div>
                      <StatusIcon className={`w-5 h-5 ${status.color}`} />
                    </div>

                    {/* Actions */}
                    {(canStart || canComplete || canMeasure) && (
                      <div className="flex gap-2 mt-4 pt-4 border-t border-neutral-100">
                        {canStart && (
                          <Button
                            onClick={() => handleStartStage(stage.stage_id)}
                            className="btn-minimal btn-primary flex-1 h-10 text-sm"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Iniciar Etapa
                          </Button>
                        )}
                        {canComplete && (
                          <Button
                            onClick={() => handleCompleteStage(stage.stage_id)}
                            className="btn-minimal btn-primary flex-1 h-10 text-sm"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Marcar Concluída
                          </Button>
                        )}
                        {canMeasure && (
                          <Button
                            onClick={() => navigate(`/project/${projectId}/measurement/${stage.stage_id}`)}
                            className="btn-minimal btn-primary flex-1 h-10 text-sm"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Realizar Medição
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Approval Info */}
                    {stage.approval && (
                      <div className={`mt-4 p-3 rounded-lg ${status.bg}`}>
                        <p className="text-sm font-medium ${status.color}">
                          {stage.approval.status === "approved" ? "✓ Aprovado" : 
                           stage.approval.status === "approved_with_reservations" ? "⚠ Aprovado com ressalvas" :
                           "✕ Rejeitado"}
                        </p>
                        {stage.approval.reservations?.length > 0 && (
                          <ul className="mt-2 text-xs text-neutral-600 space-y-1">
                            {stage.approval.reservations.map((r, i) => (
                              <li key={i}>• {r.description}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {stages.length === 0 && (
          <div className="card-subtle p-8 text-center">
            <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <p className="font-medium text-neutral-900">Nenhuma etapa definida</p>
            <p className="text-sm text-neutral-500 mt-1">O cronograma será gerado automaticamente</p>
          </div>
        )}
      </main>
    </div>
  );
}
